import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { userProfileService } from '../../services/supabaseService';
import IntakeQuestionnaire from './components/IntakeQuestionnaire';
import HealthHistoryForm from './components/HealthHistoryForm';
import FitnessBaselineAssessment from './components/FitnessBaselineAssessment';
import GoalSettingPanel from './components/GoalSettingPanel';

const ClientAssessment = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams?.get('clientId');
  const [activeSection, setActiveSection] = useState('intake');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Assessment data state
  const [assessmentData, setAssessmentData] = useState({
    intake: {},
    healthHistory: {},
    fitnessBaseline: {},
    goals: {}
  });

  // Section completion tracking
  const [sectionCompletion, setSectionCompletion] = useState({
    intake: false,
    healthHistory: false,
    fitnessBaseline: false,
    goals: false
  });

  useEffect(() => {
    if (userProfile?.role !== 'personal_trainer') {
      navigate('/dashboard');
      return;
    }
    loadClients();
  }, [user, userProfile]);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const { data: clientsList } = await userProfileService?.getTrainerClients(user?.id);
      setClients(clientsList || []);
      if (preselectedClientId && clientsList?.length > 0) {
        const preselected = clientsList?.find(c => c?.id === preselectedClientId);
        if (preselected) {
          setSelectedClient(preselected);
        }
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sections = [
    { id: 'intake', label: 'Intake Info', icon: 'ClipboardList' },
    { id: 'healthHistory', label: 'Health History', icon: 'Heart' },
    { id: 'fitnessBaseline', label: 'Fitness Baseline', icon: 'Activity' },
    { id: 'goals', label: 'Goal Setting', icon: 'Target' }
  ];

  const handleSectionDataChange = (section, data) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: data
    }));
  };

  const handleSectionComplete = (section, isComplete) => {
    setSectionCompletion(prev => ({
      ...prev,
      [section]: isComplete
    }));
  };

  const handleQuickSave = () => {
    console.log('Quick save assessment:', assessmentData);
    // TODO: Implement auto-save functionality
  };

  const handleFinalSave = async () => {
    if (!selectedClient) {
      alert('Please select a client first');
      return;
    }

    setIsSaving(true);
    try {
      console.log('Saving complete assessment for client:', selectedClient?.id, assessmentData);
      // TODO: Implement final save to database
      alert('Assessment saved successfully!');
    } catch (error) {
      console.error('Error saving assessment:', error);
      alert('Failed to save assessment');
    } finally {
      setIsSaving(false);
    }
  };

  const completedSections = Object.values(sectionCompletion)?.filter(Boolean)?.length;
  const totalSections = sections?.length;
  const completionPercentage = Math.round((completedSections / totalSections) * 100);

  return (
    <>
      <Helmet>
        <title>Client Assessment - FormForge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Client Assessment</h1>
                <p className="mt-2" style={{ color: '#6B7280' }}>Comprehensive intake evaluation and baseline measurements</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/trainer-dashboard')} iconName="ArrowLeft" iconPosition="left">Back to Dashboard</Button>
            </div>

            {/* Client Selection */}
            <div className="rounded-xl p-6 mb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg" style={{ background: 'linear-gradient(135deg, #FF6B00, #FF8C35)' }}>
                  <Icon name="Users" size={24} color="white" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#E8EAED' }}>Select Client</label>
                  <select
                    value={selectedClient?.id || ''}
                    onChange={(e) => { const client = clients?.find(c => c?.id === e?.target?.value); setSelectedClient(client); }}
                    className="w-full md:w-96 px-4 py-2 rounded-lg focus:outline-none focus:ring-2"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#E8EAED', focusRingColor: '#FF6B00' }}
                    disabled={isLoading}
                  >
                    <option value="" style={{ background: '#0A0A0B' }}>Choose a client...</option>
                    {clients?.map((client) => (
                      <option key={client?.id} value={client?.id} style={{ background: '#0A0A0B' }}>{client?.fullName} - {client?.email}</option>
                    ))}
                  </select>
                </div>
                {selectedClient && (
                  <div className="text-right">
                    <p className="text-sm" style={{ color: '#6B7280' }}>Assessment Progress</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#FF6B00' }}>{completionPercentage}%</p>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Indicator */}
            {selectedClient && (
              <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium" style={{ color: '#E8EAED' }}>Completed: {completedSections} of {totalSections} sections</span>
                  <Button variant="ghost" size="sm" onClick={handleQuickSave} iconName="Save" iconPosition="left">Quick Save</Button>
                </div>
                <div className="w-full rounded-full h-2" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <div className="h-2 rounded-full transition-all duration-300" style={{ width: `${completionPercentage}%`, background: 'linear-gradient(90deg, #FF6B00, #AAFF00)' }} />
                </div>
              </div>
            )}
          </div>

          {selectedClient ? (
            <>
              {/* Section Navigation Tabs */}
              <div className="rounded-xl p-2 mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex flex-wrap gap-2">
                  {sections?.map((section) => (
                    <button key={section?.id} onClick={() => setActiveSection(section?.id)} className="flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all" style={activeSection === section?.id ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' } : { color: '#6B7280', border: '1px solid transparent' }}>
                      <Icon name={section?.icon} size={18} />
                      <span>{section?.label}</span>
                      {sectionCompletion?.[section?.id] && <Icon name="CheckCircle" size={16} color={activeSection === section?.id ? '#FF6B00' : '#AAFF00'} />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Section Content */}
              <div className="rounded-xl p-6 mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                {activeSection === 'intake' && <IntakeQuestionnaire clientId={selectedClient?.id} data={assessmentData?.intake} onChange={(data) => handleSectionDataChange('intake', data)} onComplete={(isComplete) => handleSectionComplete('intake', isComplete)} />}
                {activeSection === 'healthHistory' && <HealthHistoryForm clientId={selectedClient?.id} data={assessmentData?.healthHistory} onChange={(data) => handleSectionDataChange('healthHistory', data)} onComplete={(isComplete) => handleSectionComplete('healthHistory', isComplete)} />}
                {activeSection === 'fitnessBaseline' && <FitnessBaselineAssessment clientId={selectedClient?.id} data={assessmentData?.fitnessBaseline} onChange={(data) => handleSectionDataChange('fitnessBaseline', data)} onComplete={(isComplete) => handleSectionComplete('fitnessBaseline', isComplete)} />}
                {activeSection === 'goals' && <GoalSettingPanel clientId={selectedClient?.id} data={assessmentData?.goals} onChange={(data) => handleSectionDataChange('goals', data)} onComplete={(isComplete) => handleSectionComplete('goals', isComplete)} />}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-end">
                <Button variant="outline" onClick={() => { const currentIndex = sections?.findIndex(s => s?.id === activeSection); if (currentIndex > 0) setActiveSection(sections?.[currentIndex - 1]?.id); }} disabled={activeSection === 'intake'} iconName="ChevronLeft" iconPosition="left">Previous</Button>
                {activeSection !== 'goals' ? (
                  <Button onClick={() => { const currentIndex = sections?.findIndex(s => s?.id === activeSection); if (currentIndex < sections?.length - 1) setActiveSection(sections?.[currentIndex + 1]?.id); }} iconName="ChevronRight" iconPosition="right">Next Section</Button>
                ) : (
                  <Button onClick={handleFinalSave} loading={isSaving} iconName="Save" iconPosition="left">Save Complete Assessment</Button>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-xl p-12 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center justify-center w-20 h-20 rounded-full mx-auto mb-4" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <Icon name="UserPlus" size={40} style={{ color: '#6B7280' }} />
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#E8EAED' }}>Select a Client to Begin Assessment</h3>
              <p style={{ color: '#6B7280' }}>Choose a client from the dropdown above to start their comprehensive intake evaluation</p>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default ClientAssessment;